import React, { useState, useRef, useContext } from "react";
import axios from "axios";
import { GlobalContext } from "../context/GlobalContext";

const DropZoneFileUploader = ({ show, handleClose, onUploadSuccess }) => {
  const { API_URL } = useContext(GlobalContext);
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // ---------- HANDLERS ----------
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(droppedFiles);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    handleClose();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      alert("Please select files to upload.");
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    try {
      setIsUploading(true);
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploadSuccess?.(response.data.files);
      // alert("Files uploaded successfully!");
      setFiles([]);
      fileInputRef.current.value = "";
      handleClose();
    } catch (error) {
      console.error("Upload error:", error);
      alert("Error uploading files. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  // ---------- RENDER ----------
  return (
    <div
      className={`modal fade ${show ? "show d-flex" : ""}`}
      style={{
        display: show ? "flex" : "none",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(6px)",
      }}
      tabIndex="-1"
      aria-hidden={!show}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{
          maxWidth: "600px",
          width: "90%",
        }}
      >
        <div
          className="modal-content text-white"
          style={{
            backgroundColor: "#121212",
            borderRadius: "16px",
            border: "1px solid #333",
            boxShadow: "0 10px 25px rgba(0,0,0,0.4)",
          }}
        >
          <div className="modal-body p-4">
            <form onSubmit={handleSave}>
              <h4 className="fw-semibold text-center mb-4">
                Upload & Attach Files
              </h4>

              {/* Dropzone Area */}
              <div
                className={`text-center border rounded-4 position-relative ${
                  dragActive ? "border-primary bg-dark bg-opacity-50" : "border-secondary"
                }`}
                style={{
                  height: "230px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="mb-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-primary"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                    <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                  </svg>
                </div>

                <p className="m-0">
                  <strong>Click to upload</strong> or drag & drop files here
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  className="d-none"
                  onChange={handleFileChange}
                />
                <p className="mt-2 text-muted small">
                  {files.length === 0
                    ? "No files selected"
                    : `${files.length} file(s) selected`}
                </p>
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div
                  className="mt-4 p-3 rounded-3"
                  style={{
                    backgroundColor: "#1e1e1e",
                    border: "1px solid #2c2c2c",
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between align-items-center border-bottom border-secondary py-2"
                    >
                      <span className="text-white">{file.name}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeFile(index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-light"
                  onClick={handleCancel}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={isUploading}
                >
                  {isUploading ? "Uploading..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DropZoneFileUploader;
